import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { get, put, type FillMode, type FillPlatform, type FillProfile, type FillProfileInput, type FillQuality, type FillSettings } from '../api'
import { Notice, Panel, ago } from '../components/ui'
import { FILL_CLASSES, FILL_MODES, FILL_MODE_HELP, FILL_MODE_SHORT, FILL_QUALITIES, FILL_QUALITY_LABEL, fillText } from '../fill'
import { ConfirmChange, FromTo } from './Confirm'
import { SettingsTabs } from './ApiTokens'

/*
 * Settings → Output bitrate (docs/bitrate-fill.md §4, levels 1 and 2): the
 * platform default and every platform profile's default with its thresholds.
 * Changes are confirmed inline, apply from the next frame without reconnects
 * and land in the audit log with the previous value.
 */

const PROFILE_NAME: Record<string, string> = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', facebook: 'Facebook', instagram: 'Instagram', custom: 'Custom RTMP' }
const profileName = (id: string) => PROFILE_NAME[id] ?? id

const selectCls = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-sm text-text focus:border-accent focus:shadow-focus focus:outline-none'
const kbpsText = (v?: number) => (v === undefined ? '—' : `${v.toLocaleString('en-US')} kbps`)

export function AdminSettings() {
  const q = useQuery({ queryKey: ['admin-fill'], queryFn: () => get<FillSettings>('/api/admin/fill') })
  return (
    <div className="space-y-6" data-testid="fill-settings">
      <div>
        <h1 className="text-xl font-medium">Settings</h1>
        <p className="text-sm text-muted">Output bitrate: how much filler data the media servers write back for each platform.</p>
      </div>
      <SettingsTabs />
      {q.error && <Notice>{(q.error as Error).message}</Notice>}
      {q.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {q.data && (
        <>
          <PlatformDefault platform={q.data.platform} modes={q.data.modes?.length ? q.data.modes : FILL_MODES} qualities={q.data.qualities?.length ? q.data.qualities : FILL_QUALITIES} />
          <Profiles settings={q.data} />
        </>
      )}
    </div>
  )
}

function PlatformDefault({ platform, modes, qualities }: { platform: FillPlatform; modes: FillMode[]; qualities: FillQuality[] }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState(platform.mode)
  const [quality, setQuality] = useState(platform.quality)
  const [margin, setMargin] = useState(String(platform.margin_pct))
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState(false)
  // A reload after saving (or another admin's change) resets the form to the server.
  useEffect(() => {
    setMode(platform.mode)
    setQuality(platform.quality)
    setMargin(String(platform.margin_pct))
  }, [platform.mode, platform.quality, platform.margin_pct])

  const marginN = Number(margin)
  const marginBad = !/^\d{1,2}$/.test(margin.trim()) || marginN < 0 || marginN > 50
  const dirty = mode !== platform.mode || quality !== platform.quality || marginN !== platform.margin_pct
  const apply = async () => {
    setBusy(true)
    setErr('')
    try {
      await put<FillPlatform>('/api/admin/fill/platform', { mode, quality, margin_pct: marginN })
      setConfirm(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3500)
      await qc.invalidateQueries({ queryKey: ['admin-fill'] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Output bitrate" aside={platform.updated_at && <span className="text-xs text-muted" data-testid="fill-platform-updated">changed {ago(platform.updated_at)}</span>}>
      <div className="flex max-w-3xl flex-col gap-4" data-testid="fill-platform" data-mode={platform.mode} data-quality={platform.quality}>
        <p className="text-sm text-text-soft">
          Platform default. Applies to every output without its own choice and without a platform profile default (below). Encoders that keep a constant bitrate pad
          their video with filler data; media servers strip it on the way in and write back as much as the mode says. Fill adds outgoing traffic.
        </p>
        <div role="radiogroup" aria-label="Default mode" className="grid gap-2 md:grid-cols-3">
          {modes.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              data-testid={`fill-platform-mode-${m}`}
              disabled={busy}
              onClick={() => {
                setMode(m)
                setErr('')
              }}
              className={`flex min-h-[44px] flex-col items-start gap-1 rounded-md border p-3 text-left ${mode === m ? 'border-live bg-live/[.12]' : 'border-line-2 hover:border-muted'}`}
            >
              <span className={`text-sm font-medium ${mode === m ? 'text-live' : 'text-text'}`}>{FILL_MODE_SHORT[m]}</span>
              <span className="text-xs text-muted">{FILL_MODE_HELP[m]}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Quality for “Fill to floor”</span>
            <select aria-label="Quality for Fill to floor" data-testid="fill-platform-quality" className={selectCls} value={quality} disabled={busy} onChange={(e) => setQuality(e.target.value as FillQuality)}>
              {qualities.map((x) => (
                <option key={x} value={x}>
                  {x === 'auto' ? "Auto (the input's resolution and frame rate)" : FILL_QUALITY_LABEL[x]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Margin above the threshold, %</span>
            <input
              aria-label="Margin above the threshold, %"
              data-testid="fill-platform-margin"
              inputMode="numeric"
              value={margin}
              disabled={busy}
              onChange={(e) => setMargin(e.target.value)}
              aria-invalid={marginBad}
              className={`tabular w-24 ${selectCls} ${marginBad ? 'border-fail' : ''}`}
            />
          </label>
          {!confirm && (
            <button
              type="button"
              data-testid="fill-platform-save"
              disabled={!dirty || marginBad || busy}
              onClick={() => setConfirm(true)}
              className="min-h-9 rounded-md border border-live bg-live/[.12] px-4 text-sm font-medium text-live disabled:border-line-2 disabled:bg-transparent disabled:text-faint"
            >
              Save…
            </button>
          )}
          {saved && (
            <span role="status" data-testid="fill-platform-saved" className="text-sm text-live">
              ✓ Saved. Applies from the next frame.
            </span>
          )}
        </div>
        {marginBad && <p className="text-xs text-fail">Margin is a whole number from 0 to 50.</p>}
        {confirm && (
          <ConfirmChange testId="fill-platform-confirm" title="Change the platform default?" busy={busy} onApply={() => void apply()} onCancel={() => setConfirm(false)}>
            {mode !== platform.mode && <FromTo label="Mode" from={FILL_MODE_SHORT[platform.mode]} to={FILL_MODE_SHORT[mode]} />}
            {quality !== platform.quality && <FromTo label="Quality" from={FILL_QUALITY_LABEL[platform.quality]} to={FILL_QUALITY_LABEL[quality]} />}
            {marginN !== platform.margin_pct && <FromTo label="Margin" from={`${platform.margin_pct}%`} to={`${marginN}%`} />}
            <p>Outputs that follow the default switch from the next frame, without reconnecting. Recorded in the log.</p>
          </ConfirmChange>
        )}
        {err && (
          <p role="alert" data-testid="fill-platform-error" className="text-sm text-fail">
            Not saved: {err}
          </p>
        )}
      </div>
    </Panel>
  )
}

function Profiles({ settings }: { settings: FillSettings }) {
  const classes = settings.classes?.length ? settings.classes : FILL_CLASSES
  return (
    <Panel title="Platform profiles" aside={<span className="text-xs text-muted">thresholds for “Fill to floor”, kbit/s</span>}>
      <p className="mb-3 max-w-3xl text-sm text-text-soft">
        A profile's default overrides the platform default for its destinations; a destination's own choice overrides both. Thresholds are the bitrate a platform
        expects for each quality; “Fill to floor” targets the threshold plus {settings.platform.margin_pct}%.
      </p>
      <div className="-mx-4 -mb-4 overflow-x-auto">
        <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal w-full min-w-[1100px]" data-testid="fill-profiles">
          <thead>
            <tr>
              <th className="px-4">Profile</th>
              <th className="px-2">Mode</th>
              <th className="px-2">Quality</th>
              {classes.map((c) => (
                <th key={c} className="px-1 text-right">
                  {c}
                </th>
              ))}
              <th className="px-4">In force</th>
              <th className="px-4" />
            </tr>
          </thead>
          <tbody>
            {settings.profiles.map((p) => (
              <ProfileRow key={p.profile} p={p} platform={settings.platform} classes={classes} modes={settings.modes?.length ? settings.modes : FILL_MODES} qualities={settings.qualities?.length ? settings.qualities : FILL_QUALITIES} />
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

type Draft = { mode: string; quality: string; thresholds: Record<string, string> }
const draftOf = (p: FillProfile, classes: string[]): Draft => ({
  mode: p.mode ?? '',
  quality: p.quality ?? '',
  thresholds: Object.fromEntries(classes.map((c) => [c, p.thresholds[c] === undefined ? '' : String(p.thresholds[c])])),
})
const badThreshold = (v: string) => v.trim() !== '' && (!/^\d+$/.test(v.trim()) || Number(v) < 1 || Number(v) > 200_000)

function ProfileRow({ p, platform, classes, modes, qualities }: { p: FillProfile; platform: FillPlatform; classes: string[]; modes: FillMode[]; qualities: FillQuality[] }) {
  const qc = useQueryClient()
  const [d, setD] = useState<Draft>(() => draftOf(p, classes))
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState(false)
  const key = JSON.stringify([p.mode, p.quality, p.thresholds])
  // The server's values changed (saved here or by another admin): start from them again.
  useEffect(() => setD(draftOf(p, classes)), [key])

  const orig = draftOf(p, classes)
  const changedClasses = classes.filter((c) => d.thresholds[c]!.trim() !== orig.thresholds[c])
  const dirty = d.mode !== orig.mode || d.quality !== orig.quality || changedClasses.length > 0
  const bad = classes.some((c) => badThreshold(d.thresholds[c]!))
  // The API replaces the whole table: send every class that has a value.
  const body = (): FillProfileInput => {
    const b: FillProfileInput = {}
    if (d.mode !== orig.mode) b.mode = (d.mode || null) as FillMode | null
    if (d.quality !== orig.quality) b.quality = (d.quality || null) as FillQuality | null
    if (changedClasses.length) b.thresholds = Object.fromEntries(classes.filter((c) => d.thresholds[c]!.trim() !== '').map((c) => [c, Number(d.thresholds[c])]))
    return b
  }
  const modeText = (m: string) => (m ? FILL_MODE_SHORT[m as FillMode] : `Platform default (${FILL_MODE_SHORT[platform.mode]})`)
  const qualityText = (x: string) => (x ? FILL_QUALITY_LABEL[x as FillQuality] : `Platform default (${FILL_QUALITY_LABEL[platform.quality]})`)
  const apply = async () => {
    setBusy(true)
    setErr('')
    try {
      await put<FillProfile>(`/api/admin/fill/profiles/${encodeURIComponent(p.profile)}`, body())
      setConfirm(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3500)
      await qc.invalidateQueries({ queryKey: ['admin-fill'] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const name = profileName(p.profile)
  return (
    <>
      <tr data-testid={`fill-profile-${p.profile}`} data-mode={p.mode ?? ''} data-effective={p.effective_mode}>
        <td className="px-4 py-2 font-medium">{name}</td>
        <td className="px-2 py-2">
          <select aria-label={`${name}: mode`} data-testid={`fill-profile-mode-${p.profile}`} className={selectCls} value={d.mode} disabled={busy || confirm} onChange={(e) => setD({ ...d, mode: e.target.value })}>
            <option value="">{modeText('')}</option>
            {modes.map((m) => (
              <option key={m} value={m}>
                {FILL_MODE_SHORT[m]}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2">
          <select aria-label={`${name}: quality`} data-testid={`fill-profile-quality-${p.profile}`} className={selectCls} value={d.quality} disabled={busy || confirm} onChange={(e) => setD({ ...d, quality: e.target.value })}>
            <option value="">{qualityText('')}</option>
            {qualities.map((x) => (
              <option key={x} value={x}>
                {FILL_QUALITY_LABEL[x]}
              </option>
            ))}
          </select>
        </td>
        {classes.map((c) => (
          <td key={c} className="px-1 py-2 text-right">
            <input
              aria-label={`${name}: ${c}, kbit/s`}
              data-testid={`fill-threshold-${p.profile}-${c}`}
              inputMode="numeric"
              value={d.thresholds[c]}
              disabled={busy || confirm}
              aria-invalid={badThreshold(d.thresholds[c]!)}
              onChange={(e) => setD({ ...d, thresholds: { ...d.thresholds, [c]: e.target.value } })}
              className={`tabular w-20 rounded-md border bg-bg px-2 py-1.5 text-right font-mono text-xs ${badThreshold(d.thresholds[c]!) ? 'border-fail' : d.thresholds[c]!.trim() !== orig.thresholds[c] ? 'border-accent' : 'border-line-2'}`}
            />
          </td>
        ))}
        <td className="px-4 py-2 text-xs text-text-soft" data-testid={`fill-profile-effective-${p.profile}`}>
          {fillText({ mode: p.effective_mode, quality: p.effective_quality })}
          <span className="block text-muted">{p.mode === null && p.quality === null ? 'from the platform default' : 'profile default'}</span>
        </td>
        <td className="px-4 py-2 text-right whitespace-nowrap">
          {saved && (
            <span role="status" data-testid={`fill-profile-saved-${p.profile}`} className="mr-2 text-xs text-live">
              ✓ Saved
            </span>
          )}
          {dirty && !confirm && (
            <button type="button" data-testid={`fill-profile-reset-${p.profile}`} className="mr-2 min-h-9 px-1 text-sm text-muted hover:text-text" onClick={() => setD(orig)}>
              Revert
            </button>
          )}
          <button
            type="button"
            data-testid={`fill-profile-save-${p.profile}`}
            disabled={!dirty || bad || busy || confirm}
            onClick={() => setConfirm(true)}
            className="min-h-9 rounded-md border border-live bg-live/[.12] px-3 text-sm font-medium text-live disabled:border-line-2 disabled:bg-transparent disabled:text-faint"
          >
            Save…
          </button>
        </td>
      </tr>
      {(confirm || err || bad) && (
        <tr>
          <td colSpan={classes.length + 5} className="px-4 pb-3">
            {bad && <p className="text-xs text-fail">A threshold is a whole number of kbit/s from 1 to 200 000; leave it empty to drop the class.</p>}
            {confirm && (
              <ConfirmChange testId="fill-profile-confirm" title={`Change the ${name} profile?`} busy={busy} onApply={() => void apply()} onCancel={() => setConfirm(false)}>
                {d.mode !== orig.mode && <FromTo label="Mode" from={modeText(orig.mode)} to={modeText(d.mode)} />}
                {d.quality !== orig.quality && <FromTo label="Quality" from={qualityText(orig.quality)} to={qualityText(d.quality)} />}
                {changedClasses.map((c) => (
                  <FromTo key={c} label={c} from={kbpsText(p.thresholds[c])} to={d.thresholds[c]!.trim() === '' ? 'removed' : kbpsText(Number(d.thresholds[c]))} />
                ))}
                <p>{name} outputs that follow the profile switch from the next frame, without reconnecting. Recorded in the log.</p>
              </ConfirmChange>
            )}
            {err && (
              <p role="alert" data-testid={`fill-profile-error-${p.profile}`} className="text-sm text-fail">
                Not saved: {err}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
